import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLinkNavigation} from '../../../../../components/common/navigation/useLinkNavigation';
import {IntegrationTypeModel} from '../../../../../models/api/integrationTypesModel';
import {buildSettingsBillingUrl} from '../../../../../models/url/settingsUrlHelpers';

/*
 * Props.
 */

interface SettingsAppsDetailsUpsellBannerProps {
  integrationType: IntegrationTypeModel;
  isIntegrationAvailableOnPlan: boolean;
  isIntegrationUncounted: boolean;
  isAtMaxEnabledIntegrationLimit: boolean;
  isBillingHidden: boolean;
  isBilledThroughOtherCompany: boolean;
  isGlobalAdmin: boolean;
}

/*
 * Style.
 */

const StyledBannerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  background: ${(p) => p.theme.palette.yellow.shade20};
  padding: 16px;
  border-radius: 8px;
  gap: 8px;
  margin-bottom: 16px;
`;

const StyledContentWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.verySmall};
`;

const StyledContentDiv = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  margin-top: 8px;
  max-width: 390px;
`;

const StyledActionWrapperDiv = styled.div`
  display: flex;
  margin-top: 16px;

  &:empty {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  margin-left: -14px;
  font-size: ${fontSizes.small};
`;

/*
 * Component.
 */

export const SettingsAppsDetailsUpsellBanner: FC<SettingsAppsDetailsUpsellBannerProps> = (props) => {
  const {isIntegrationUncounted, isAtMaxEnabledIntegrationLimit, isIntegrationAvailableOnPlan} = props;
  const {navigateToLink} = useLinkNavigation();

  // If the integration is uncounted, the user can enable it without any issues.
  if (isIntegrationUncounted) {
    return null;
  }

  // If the integration is available and they are not at the max allowed integrations limit.
  if (isIntegrationAvailableOnPlan && !isAtMaxEnabledIntegrationLimit) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledBannerWrapperDiv>
          <Icon name="diamondSmall" color={theme.palette.yellow.shade40} />
          <StyledContentWrapperDiv>
            <StyledTitleDiv>
              <Trans id="settings-apps-details-upsell-banner-title">Upgrade required</Trans>
            </StyledTitleDiv>
            <StyledContentDiv>{renderContent(props)}</StyledContentDiv>
            <StyledActionWrapperDiv>{maybeRenderAction(props, navigateToLink)}</StyledActionWrapperDiv>
          </StyledContentWrapperDiv>
        </StyledBannerWrapperDiv>
      )}
    </UserThemeConsumer>
  );
};

/*
 * Helper.
 */

function maybeRenderAction(props: SettingsAppsDetailsUpsellBannerProps, navigateToLink: HandlerOf<string>) {
  const {isGlobalAdmin, isBilledThroughOtherCompany, isBillingHidden} = props;

  if (!isGlobalAdmin || isBilledThroughOtherCompany || isBillingHidden) {
    return null;
  }
  return (
    <StyledButton variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(buildSettingsBillingUrl())}>
      <Trans id="settings-apps-details-upsell-banner-upgrade-plan-button">Upgrade plan</Trans>
    </StyledButton>
  );
}

function renderContent(props: SettingsAppsDetailsUpsellBannerProps) {
  const {isIntegrationAvailableOnPlan, isAtMaxEnabledIntegrationLimit} = props;

  if (!isIntegrationAvailableOnPlan) {
    return renderIntegrationNotAvailableOnPlan(props);
  }
  if (isAtMaxEnabledIntegrationLimit) {
    return renderAtMaxIntegrations(props);
  }
  return null;
}

function renderIntegrationNotAvailableOnPlan(props: SettingsAppsDetailsUpsellBannerProps) {
  const {integrationType} = props;
  const baseMessage: React.ReactNode = (
    <Trans id="settings-apps-details-upsell-banner-not-available-on-plan">
      {integrationType.name} is not available on your plan.
    </Trans>
  );
  return renderFullBannerMessage(baseMessage, props);
}

function renderAtMaxIntegrations(props: SettingsAppsDetailsUpsellBannerProps) {
  const baseMessage: React.ReactNode = (
    <Trans id="settings-apps-details-upsell-banner-too-many-integrations">
      You have too many integrations for this plan.
    </Trans>
  );
  return renderFullBannerMessage(baseMessage, props);
}

function renderFullBannerMessage(baseMessage: React.ReactNode, props: SettingsAppsDetailsUpsellBannerProps) {
  const {integrationType, isGlobalAdmin, isBilledThroughOtherCompany, isBillingHidden} = props;

  // User is not a global admin or they are billed through a different company so they do not have access
  // to the billing tab in settings.
  if (!isGlobalAdmin || isBilledThroughOtherCompany) {
    return (
      <>
        {baseMessage}{' '}
        <Trans id="settings-apps-details-upsell-banner-contact-admin">
          Please contact an administrator to upgrade your plan.
        </Trans>
      </>
    );
  }

  // If the billing is hidden this means they would need to contact their Front admin.
  if (isBillingHidden) {
    return (
      <>
        {baseMessage}{' '}
        <Trans id="settings-apps-details-upsell-banner-contact-account-manager">
          Please contact your Front account manager to upgrade your plan.
        </Trans>
      </>
    );
  }

  // They are a global admin who can change billing.
  return (
    <>
      {baseMessage}{' '}
      <Trans id="settings-apps-details-upsell-banner-please-upgrade">
        Please upgrade your plan to enable {integrationType.name}.
      </Trans>
    </>
  );
}
